import { ChartRangeSelector } from '@/components/shared/chartRangeSelector'
import { VALID_CHART_PERIODS, useSettingsContext } from '@/context/settings'
import { useHistoricalQuery } from '@/queries/historical.query'
import type { Vault } from '@/types/vault'
import { getApyTypeKey } from '@/utils/apy'
import { PerformanceCharts, type PerformanceChartsProps } from './sections/performanceCharts'

interface Props {
  vault: Vault
}

export const StrategyPerformance = ({ vault }: Props) => {
  const { performanceChartsProps, chartsPeriod, handleChartsPeriod } = useStrategyPerformanceData(vault)

  const rangeSelector = (
    <div className="flex items-center gap-2 text-sm">
      <span>Chart range</span>
      <ChartRangeSelector
        chartPeriods={VALID_CHART_PERIODS}
        chartsPeriod={chartsPeriod}
        onRangeSelect={handleChartsPeriod}
        className="h-6"
      />
    </div>
  )

  return <PerformanceCharts {...performanceChartsProps} rangeSelector={rangeSelector} />
}

const useStrategyPerformanceData = (vault: Vault) => {
  const { apyRange, chartsPeriod, includeRewards, handleChartsPeriod } = useSettingsContext()
  const { apyComposite } = vault
  const { data: chartData } = useHistoricalQuery({ vault, apyRange, chartsPeriod })

  const intrinsicApy = apyComposite?.intrinsicApy[apyRange][getApyTypeKey({ includeRewards })] ?? null
  const latestSharePrice = chartData.at(-1)?.sharePrice ?? null
  const firstSharePrice = chartData[0]?.sharePrice ?? null
  const sharePricePeriodChange =
    chartData.length > 1 && latestSharePrice != null && firstSharePrice != null && firstSharePrice !== 0
      ? ((latestSharePrice - firstSharePrice) / firstSharePrice) * 100
      : null
  const latestTvl = chartData.at(-1)?.tvl ?? null
  const firstTvl = chartData[0]?.tvl ?? null
  const tvlPeriodChange =
    chartData.length > 1 && latestTvl != null && firstTvl != null && firstTvl !== 0
      ? ((latestTvl - firstTvl) / firstTvl) * 100
      : null

  const performanceChartsProps: Omit<PerformanceChartsProps, 'rangeSelector'> = {
    vault,
    apyRange,
    includeRewards,
    intrinsicApy,
    chartData,
    latestSharePrice,
    sharePricePeriodChange,
    tvlPeriodChange,
  }

  return { performanceChartsProps, chartsPeriod, handleChartsPeriod }
}
